import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Sitemap.css';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import sitemapManifest from '../../data/sitemapManifest.generated';
import { INDEX_ROBOTS } from '../../seo/robotsMeta';
import { absoluteUrl } from '../../seo/siteOrigin';
import FAQSection from '../FAQSection';

const Sitemap = () => {
  const sitemapData = sitemapManifest.categories;
  const canonical = absoluteUrl('/sitemap');
  const lastUpdated = new Date().toISOString().split('T')[0];

  const faqs = [
    {
      question: 'What is the purpose of this HTML sitemap?',
      answer: 'This HTML sitemap is designed to help users quickly find important fulfilment services, pricing, and company information on the Pick Pack Pro website. For search engine crawlers, we also maintain an XML version.'
    },
    {
      question: 'Where is Pick Pack Pro located?',
      answer: 'Pick Pack Pro operates from a central UK fulfilment hub in Milton Keynes, perfectly positioned for next-day nationwide delivery.'
    },
    {
      question: 'How do I get a quote for fulfilment services?',
      answer: 'You can navigate to our Contact Us page via the "Company" section above, or click the "Contact Us" button in the Quick Actions section.'
    }
  ];

  return (
    <div className="sitemap-page">
      <Helmet>
        <meta name="robots" content={INDEX_ROBOTS} />
        <meta name="googlebot" content={INDEX_ROBOTS} />
        <meta httpEquiv="X-Robots-Tag" content={INDEX_ROBOTS} />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebPage",
                "@id": canonical,
                "url": canonical,
                "name": "Pick Pack Pro Fulfilment Services HTML Sitemap",
                "description": "Complete HTML sitemap of Pick Pack Pro. Browse all fulfilment services, e-commerce integrations, and 3PL pricing.",
                "inLanguage": "en-GB",
                "datePublished": "2026-09-01T00:00:00+00:00",
                "dateModified": `${lastUpdated}T00:00:00+00:00`,
                "author": {
                  "@type": "Organization",
                  "name": "Pick Pack Pro",
                  "@id": "https://www.pickpackpro.co.uk/#organization"
                }
              },
              {
                "@type": "Article",
                "@id": `${canonical}#article`,
                "headline": "Pick Pack Pro HTML Site Navigation",
                "author": {
                  "@type": "Organization",
                  "name": "Pick Pack Pro",
                  "@id": "https://www.pickpackpro.co.uk/#organization"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "Pick Pack Pro",
                  "@id": "https://www.pickpackpro.co.uk/#organization"
                },
                "datePublished": "2026-09-01T00:00:00+00:00",
                "dateModified": `${lastUpdated}T00:00:00+00:00`
              },
              {
                "@type": "Organization",
                "@id": "https://www.pickpackpro.co.uk/#organization",
                "name": "Pick Pack Pro",
                "url": "https://www.pickpackpro.co.uk/",
                "sameAs": [
                  "https://www.linkedin.com/company/pick-pack-pro/",
                  "https://www.pickpackpro.co.uk"
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://www.pickpackpro.co.uk/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Sitemap",
                    "item": canonical
                  }
                ]
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="sitemap-hero">
        <div className="sitemap-hero-container">
          <div className="sitemap-hero-content">
            <div className="sitemap-badge">
              <span className="sitemap-badge-dot"></span>
              Site Navigation
            </div>
            <h1 className="sitemap-hero-title">
              Complete HTML Site <span className="sitemap-hero-highlight">Navigation</span>
            </h1>
            <p className="sitemap-hero-subtitle">
              Easily navigate Pick Pack Pro’s comprehensive logistics and ecommerce fulfilment resources.
            </p>
          </div>
        </div>
      </section>

      {/* AEO Summary Block */}
      <section style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 1rem' }}>
        <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', borderLeft: '4px solid #28479e' }}>
          <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '8px' }}>
            <strong>Author:</strong> Pick Pack Pro Editorial Team | <strong>Last Updated:</strong> {lastUpdated}
          </p>
          <h2 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '12px', marginTop: 0 }}>
            HTML Sitemap Summary
          </h2>
          <p style={{ margin: 0, color: '#334155', lineHeight: 1.6 }}>
            <strong>Top Summary & Definition:</strong> This HTML sitemap provides a clear, structured overview of all pages on the Pick Pack Pro website. It is designed to help e-commerce sellers, B2B brands, and site visitors quickly locate key 3PL logistics services, pricing details, and our comprehensive blog guides. For automated crawler navigation, search engines use our <a href="https://developers.google.com/search/docs/crawling-indexing/sitemaps/overview" target="_blank" rel="noopener noreferrer" style={{ color: '#28479e', textDecoration: 'underline' }}>standardized XML protocol</a> file.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="sitemap-main">
        <div className="sitemap-container">
          
          {/* Services */}
          <div className="sitemap-category">
            <div className="sitemap-category-header">
              <div className="sitemap-category-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  <path d="M3 8l9-5 9 5M3 8l9 5 9-5"/>
                </svg>
              </div>
              <h2 className="sitemap-category-title">Services</h2>
            </div>
            <div className="sitemap-links-grid">
              {sitemapData.services.map((item) => (
                <Link key={item.path} to={item.path} className="sitemap-link-card">
                  <div className="sitemap-link-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="8" width="18" height="12" rx="2"/>
                      <path d="M3 8l9-5 9 5"/>
                    </svg>
                  </div>
                  <div className="sitemap-link-content">
                    <h3 className="sitemap-link-title">{item.title}</h3>
                    <p className="sitemap-link-desc">{item.description}</p>
                    <span className="sitemap-link-path">{item.path}</span>
                  </div>
                  <div className="sitemap-link-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Company */}
          <div className="sitemap-category">
            <div className="sitemap-category-header">
              <div className="sitemap-category-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h2 className="sitemap-category-title">Company</h2>
            </div>
            <div className="sitemap-links-grid">
              {sitemapData.company.map((item) => (
                <Link key={item.path} to={item.path} className="sitemap-link-card">
                  <div className="sitemap-link-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                    </svg>
                  </div>
                  <div className="sitemap-link-content">
                    <h3 className="sitemap-link-title">{item.title}</h3>
                    <p className="sitemap-link-desc">{item.description}</p>
                    <span className="sitemap-link-path">{item.path}</span>
                  </div>
                  <div className="sitemap-link-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Blog articles */}
          <div className="sitemap-category">
            <div className="sitemap-category-header">
              <div className="sitemap-category-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                </svg>
              </div>
              <h2 className="sitemap-category-title">Blog articles</h2>
            </div>
            <div className="sitemap-links-grid">
              {sitemapData.blogs.map((item) => (
                <Link key={item.path} to={item.path} className="sitemap-link-card">
                  <div className="sitemap-link-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                  </div>
                  <div className="sitemap-link-content">
                    <h3 className="sitemap-link-title">{item.title}</h3>
                    <p className="sitemap-link-desc">{item.description}</p>
                    <span className="sitemap-link-path">{item.path}</span>
                  </div>
                  <div className="sitemap-link-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="sitemap-category">
            <div className="sitemap-category-header">
              <div className="sitemap-category-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10 9 9 9 8 9"/>
                </svg>
              </div>
              <h2 className="sitemap-category-title">Legal & Policies</h2>
            </div>
            <div className="sitemap-links-grid">
              {sitemapData.legal.map((item) => (
                <Link key={item.path} to={item.path} className="sitemap-link-card">
                  <div className="sitemap-link-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div className="sitemap-link-content">
                    <h3 className="sitemap-link-title">{item.title}</h3>
                    <p className="sitemap-link-desc">{item.description}</p>
                    <span className="sitemap-link-path">{item.path}</span>
                  </div>
                  <div className="sitemap-link-arrow">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <FAQSection
            headingId="sitemap-faq"
            className="faqs-section"
            title="Frequently Asked Questions"
            faqs={faqs}
          />

          {/* Quick Actions */}
          <div className="sitemap-cta-section">
            <div className="sitemap-cta-box">
              <h2 className="sitemap-cta-title">Need Help Finding Something?</h2>
              <p className="sitemap-cta-text">Can't find what you're looking for? Our team is here to help you navigate our services.</p>
              <div className="sitemap-cta-buttons">
                <Link to="/contact-fulfillment-services" className="sitemap-cta-btn sitemap-cta-primary">
                  Contact Us
                </Link>
                <Link to="/fulfillment-frequently-asked-questions" className="sitemap-cta-btn sitemap-cta-secondary">
                  View FAQs
                </Link>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      {/* Footer Section */}
      <Footer />
    </div>
  );
};

export default Sitemap;
